import { useEffect } from "react";
import { Box, Typography, Button, Card, CardContent, Grid } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useThemeMode } from "@context/ThemeContext";
import { useAppDispatch, useAppSelector } from "@/store";
import { fetchFiles } from "@/store/filesSlice";
import { FileUpload } from "@components/FileUpload";
import { FileTable } from "@components/FileTable";
import { PageContainer } from "@components/PageContainer";
import { DASHBOARD_INFO_CARDS } from "@/constants/dashboardInfoCards";
import { DASHBOARD_RECENT_FILES_LIMIT, DASHBOARD_UPLOAD_SCROLL_Y } from "@/constants/app";

export default function Dashboard() {
  const { t } = useTranslation();
  const { mode } = useThemeMode();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { files } = useAppSelector((s) => s.files);

  useEffect(() => {
    dispatch(fetchFiles({ limit: DASHBOARD_RECENT_FILES_LIMIT }));
  }, [dispatch]);

  return (
    <PageContainer>
      <Card sx={{ mb: 3 }}>
        <CardContent
          sx={{
            p: { xs: 3, sm: 4 },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ mb: { xs: 3, md: 0 } }}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                fontWeight: "bold",
              }}
            >
              {t("dashboard.title")}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                maxWidth: 480,
              }}
            >
              {t("dashboard.subtitle")}
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            size="large"
            sx={{ px: 3, py: 1.5, whiteSpace: "nowrap" }}
            onClick={() => window.scrollTo({ top: DASHBOARD_UPLOAD_SCROLL_Y, behavior: "smooth" })}
          >
            {t("dashboard.newUpload")}
          </Button>
        </CardContent>
      </Card>
      <FileUpload />
      <Box sx={{ mt: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
            }}
          >
            {t("dashboard.recentUploads")}
          </Typography>
          <Button size="small" color="secondary" onClick={() => navigate("/files")}>
            {t("dashboard.viewAll")}
          </Button>
        </Box>
        <Card>
          <FileTable files={files.slice(0, DASHBOARD_RECENT_FILES_LIMIT)} />
        </Card>
      </Box>
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {DASHBOARD_INFO_CARDS.map((card) => (
          <Grid size={{ xs: 12, md: 4 }} key={card.i18nKey}>
            <Card sx={{ height: "100%" }}>
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: mode === "light" ? card.bg : card.darkBg,
                    borderRadius: 3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2,
                  }}
                >
                  <card.icon sx={{ color: card.color }} />
                </Box>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{
                    fontWeight: "bold",
                  }}
                >
                  {t(`${card.i18nKey}.title`)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  {t(`${card.i18nKey}.desc`)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Box
        sx={{
          mt: 6,
          pt: 3,
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          justifyContent: "space-between",
          color: "text.secondary",
          fontSize: 14,
        }}
      >
        <Typography variant="body2">
          &copy; {new Date().getFullYear()} OneTime Upload. All rights reserved.
        </Typography>
      </Box>
    </PageContainer>
  );
}
